import React from 'react';
import { Container, Typography, Box, Grid, Button, Paper, useTheme } from '@mui/material';
import { Bookmark, Visibility, Gavel, Groups, Handshake } from '@mui/icons-material';
import { motion } from "framer-motion";

// Import images
import farmersBankLogo from '../images/Farmersbank_logo.jpg';
import aboutHeroImage from '../images/b.jpg';
import visionImage from '../images/s.jpg';
import missionImage from '../images/b3.png';
import teamImage from '../images/a.jpg';

// Define color palette
const colors = {
  primary: '#132714',
  secondary: '#57a649',
  accent: '#f5a623',
  lightBg: '#f8fffb',
  textDark: '#132714',
  textLight: '#ffffff'
};

const About = () => {
  const theme = useTheme();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };

  const slideInFromLeft = {
    hidden: { x: -50, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const slideInFromRight = {
    hidden: { x: 50, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
        {/* Hero Section */}
        <motion.div
          variants={fadeIn}
          style={{
            height: '70vh',
            minHeight: '500px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: `linear-gradient(to right, rgba(19, 39, 20, 0.9), rgba(87, 166, 73, 0.7)), url(${aboutHeroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Container>
            <motion.div variants={itemVariants}>
              <Typography 
                variant="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                About <Box component="span" sx={{ color: colors.accent }}>Farmers Bank</Box>
              </Typography>
              <Typography 
                variant="h5"
                sx={{ 
                  maxWidth: '800px',
                  mx: 'auto',
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  fontWeight: 300,
                  opacity: 0.9
                }}
              >
                Eswatini's premier agricultural financial institution
              </Typography>
            </motion.div>
          </Container>
        </motion.div>

        <Container sx={{ py: 10 }}>
          {/* Introduction Section */}
          <Grid container spacing={6} sx={{ mb: 10 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                variants={slideInFromLeft}
                viewport={{ once: true }}
              >
                <Typography 
                  variant="h2" 
                  gutterBottom 
                  sx={{ 
                    color: colors.primary,
                    fontWeight: 700,
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -10,
                      left: 0,
                      width: '80px',
                      height: '4px',
                      backgroundColor: colors.secondary,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Our Story
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    mb: 3,
                    color: 'text.secondary'
                  }}
                >
                  Farmers Bank is Eswatini's newest Commercial Bank, granted a commercial banking licence by the Central Bank of Eswatini in 2018.
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3, color: 'text.secondary' }}>
                  We aim to revolutionize the banking experience and improve the lives of EmaSwati people through innovative financial solutions.
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, fontWeight: 600, color: colors.primary }}>
                  Our mission is to bring Financial Inclusion to the Agricultural Sector in Eswatini.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 4 }}>
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      boxShadow: `0 4px 12px ${colors.secondary}80`,
                      transition: 'all 0.3s ease',
                      backgroundColor: colors.secondary,
                      fontWeight: 600
                    }}
                  >
                    Learn More
                  </Button>
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="outlined"
                    color="primary"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      borderWidth: '2px',
                      borderColor: colors.secondary,
                      color: colors.secondary,
                      fontWeight: 600,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Our Team
                  </Button>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                variants={slideInFromRight}
                viewport={{ once: true }}
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: '0 30px 60px -10px rgba(0,0,0,0.3)'
                }}
                style={{ 
                  position: 'relative',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  height: '100%',
                  minHeight: 300,
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                }}
              >
                <Box
                  component="img"
                  src={teamImage}
                  alt="Farmers Bank Team"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: 3,
                  background: `linear-gradient(to top, ${colors.primary}, transparent)`,
                  color: 'white',
                  zIndex: 2,
                  textAlign: 'left'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Our Leadership Team</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Dedicated professionals driving innovation</Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          {/* Vision and Mission Section */}
          <Box sx={{ mb: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <motion.div
                variants={itemVariants}
                viewport={{ once: true }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: colors.secondary,
                    fontWeight: 600,
                    letterSpacing: 1,
                    mb: 1
                  }}
                >
                  OUR GUIDING PRINCIPLES
                </Typography>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 700,
                    color: colors.primary,
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -15,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '100px',
                      height: '4px',
                      background: colors.secondary,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Vision & Mission
                </Typography>
              </motion.div>
            </Box>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ 
                    delay: 0.1,
                    duration: 0.5,
                    type: "spring",
                    stiffness: 100
                  }}
                  whileHover={{ scale: 1.02 }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      background: 'white',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      border: `1px solid ${colors.secondary}20`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                        borderColor: `${colors.secondary}40`
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={3}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: `${colors.secondary}10`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 3,
                          color: colors.secondary
                        }}
                      >
                        <Visibility sx={{ fontSize: 40 }} />
                      </Box>
                      <Typography variant="h3" sx={{ color: colors.primary, fontWeight: 700 }}>
                        Our Vision
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
                      To be The Safe Bank™ by being the leading financial platform for agribusiness and sustainability projects through secure investments.
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ 
                    delay: 0.2,
                    duration: 0.5,
                    type: "spring",
                    stiffness: 100
                  }}
                  whileHover={{ scale: 1.02 }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      background: 'white',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      border: `1px solid ${colors.secondary}20`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                        borderColor: `${colors.secondary}40`
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={3}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: `${colors.secondary}10`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 3,
                          color: colors.secondary
                        }}
                      >
                        <Bookmark sx={{ fontSize: 40 }} />
                      </Box>
                      <Typography variant="h3" sx={{ color: colors.primary, fontWeight: 700 }}>
                        Our Mission
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
                      To enrich the lives of our customers by providing liquidity to the agricultural ecosystem and funding sustainable projects that ensure food, water and energy security, employment and poverty alleviation.
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* Core Values Section */}
          <Box sx={{ mb: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <motion.div
                variants={itemVariants}
                viewport={{ once: true }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: colors.secondary,
                    fontWeight: 600,
                    letterSpacing: 1,
                    mb: 1
                  }}
                >
                  OUR FOUNDATION
                </Typography>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 700,
                    color: colors.primary,
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -15,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '100px',
                      height: '4px',
                      background: colors.secondary,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Core Values
                </Typography>
                <Typography variant="h5" sx={{ mt: 3, color: 'text.secondary', fontWeight: 300 }}>
                  The principles that guide everything we do
                </Typography>
              </motion.div>
            </Box>
            
            {/* Centered Grid with equal width cards */}
            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  icon: <Gavel sx={{ fontSize: 40, color: colors.secondary }} />,
                  title: "Honesty & Integrity",
                  description: "We are dedicated to the practice of the utmost ethical standards and honour our commitments."
                },
                {
                  icon: <Groups sx={{ fontSize: 40, color: colors.secondary }} />,
                  title: "Empowerment",
                  description: "We meet our clients at their level and promote financial inclusion to improve their financial status."
                },
                {
                  icon: <Bookmark sx={{ fontSize: 40, color: colors.secondary }} />,
                  title: "Accountability",
                  description: "We operate transparently and hold ourselves accountable to our stakeholders."
                },
                {
                  icon: <Handshake sx={{ fontSize: 40, color: colors.secondary }} />,
                  title: "Collaboration",
                  description: "We establish mutually beneficial relationships based on trust and mutual success."
                },
                {
                  icon: <Handshake sx={{ fontSize: 40, color: colors.secondary }} />,
                  title: "Service",
                  description: "We seek to understand our people and clients and deliver solutions to meet their needs."
                }
              ].map((value, index) => (
                <Grid item xs={12} sm={10} md={8} lg={6} key={`value-${index}`}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ 
                      delay: index * 0.1,
                      duration: 0.5,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Paper
                      elevation={3}
                      sx={{
                        p: 4,
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                        border: `1px solid ${colors.secondary}20`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                          borderColor: `${colors.secondary}40`
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        width: '100%',
                        maxWidth: '600px',
                        mx: 'auto'
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: `${colors.secondary}10`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3
                        }}
                      >
                        {value.icon}
                      </Box>
                      <Typography variant="h4" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                        {value.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary' }}>
                        {value.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </motion.div>
  );
};

export default About;